import { useCallback, useEffect, useState } from 'react'
import { digitsOnly } from '@/utils/cpf'
import type { CaptchaConfig } from '@/components/AleloCaptcha'

export type ResetChannel = 'email' | 'sms'

type ResetRequestSdk = {
  resetPassword: (data: Record<string, string>) => Promise<void>
  backToLogin: () => Promise<void>
  screen?: {
    isCaptchaAvailable?: boolean
    captcha?: CaptchaConfig | null
    captchaProvider?: string | null
    captchaSiteKey?: string | null
    captchaImage?: string | null
  }
  transaction?: {
    state?: string
    hasFlexibleIdentifier?: boolean
  }
}

async function createSdk(): Promise<ResetRequestSdk | null> {
  try {
    const mod = await import('@auth0/auth0-acul-js/reset-password-request')
    return new mod.default() as unknown as ResetRequestSdk
  } catch {
    return null
  }
}

function resolveCaptcha(sdk: ResetRequestSdk | null): CaptchaConfig | null {
  if (!sdk?.screen) return null
  const fromObj = sdk.screen.captcha
  if (fromObj?.provider) {
    return {
      provider: fromObj.provider,
      siteKey: fromObj.siteKey ?? sdk.screen.captchaSiteKey,
      image: fromObj.image ?? sdk.screen.captchaImage,
    }
  }
  if (sdk.screen.isCaptchaAvailable || sdk.screen.captchaProvider) {
    return {
      provider: sdk.screen.captchaProvider,
      siteKey: sdk.screen.captchaSiteKey,
      image: sdk.screen.captchaImage,
    }
  }
  return null
}

/**
 * O SDK remapeia `username` → `email` quando `hasFlexibleIdentifier=false`.
 * Com identificador = CPF isso gera POST inválido (400). Forçamos `username`.
 */
function submitUsernameForm(payload: Record<string, string>) {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = ''
  for (const [key, value] of Object.entries(payload)) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = key
    input.value = value
    form.appendChild(input)
  }
  const sdkField = document.createElement('input')
  sdkField.type = 'hidden'
  sdkField.name = 'acul-sdk'
  sdkField.value = '@auth0/auth0-acul-js@1.6.0'
  form.appendChild(sdkField)
  document.body.appendChild(form)
  form.submit()
}

export function useResetPasswordRequestManager() {
  const [sdk, setSdk] = useState<ResetRequestSdk | null>(null)
  const [error, setError] = useState<string>()
  const [captchaToken, setCaptchaToken] = useState<string>()

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const instance = await createSdk()
      if (!cancelled) setSdk(instance)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const captchaConfig = resolveCaptcha(sdk)
  const captchaRequired = Boolean(captchaConfig?.provider)
  const onCaptchaToken = useCallback((token: string | undefined) => {
    setCaptchaToken(token)
  }, [])

  /** Reset nativo por e-mail. O SMS vai pelo BFF (`utils/passwordResetApi`). */
  async function handleContinue(usernameRaw: string) {
    setError(undefined)
    const username = digitsOnly(usernameRaw)
    if (username.length !== 11) {
      setError('Informe um CPF válido com 11 dígitos.')
      throw new Error('CPF_INVALID')
    }
    if (captchaRequired && !captchaToken) {
      setError('Complete a verificação de segurança para continuar.')
      throw new Error('CAPTCHA_REQUIRED')
    }

    const instance = sdk ?? (await createSdk())
    const state =
      instance?.transaction?.state ||
      (
        window as unknown as {
          universal_login_context?: { transaction?: { state?: string } }
        }
      ).universal_login_context?.transaction?.state

    if (!instance && !state) {
      console.info('[preview reset-password-request]', { username })
      window.dispatchEvent(
        new CustomEvent('alelo:acul-preview', {
          detail: {
            screen: 'reset-password-request',
            action: 'resetPassword',
            data: { username },
          },
        }),
      )
      return
    }

    const payload: Record<string, string> = {
      username,
      ...(captchaToken ? { captcha: captchaToken } : {}),
    }

    if (state && instance?.transaction?.hasFlexibleIdentifier === false) {
      submitUsernameForm({ ...payload, state })
      return
    }

    if (instance?.resetPassword && instance.transaction?.hasFlexibleIdentifier) {
      await instance.resetPassword(payload)
      return
    }

    if (state) {
      submitUsernameForm({ ...payload, state })
      return
    }

    if (instance?.resetPassword) {
      await instance.resetPassword(payload)
    }
  }

  async function handleBack() {
    const instance = sdk ?? (await createSdk())
    if (instance?.backToLogin) {
      await instance.backToLogin()
      return
    }
    window.location.search = '?screen=login-id'
  }

  return {
    handleContinue,
    handleBack,
    captchaConfig,
    captchaReady: !captchaRequired || Boolean(captchaToken),
    onCaptchaToken,
    error,
  }
}
